<template>
  <div>
    <div v-if="redemption.appliedCodeName" class="flex items-center justify-between gap-x-3 border border-gold4 bg-light-bg-inset px-3 py-3 text-sm text-light-fg-default" data-testid="applied-code">
      <p>
        <span aria-hidden="true" class="mr-1 text-gold1">✓</span><span class="tracking-wider">{{ redemption.appliedCodeName }}</span> applied
      </p>
      <button class="shrink-0 text-sm text-gold1 underline underline-offset-2" type="button" @click="onRemove">Remove</button>
    </div>
    <template v-else>
      <div class="flex items-stretch gap-x-2">
        <input
          v-if="isCompact"
          ref="compactInput"
          aria-label="Apply Code"
          autocomplete="off"
          class="h-10 min-w-0 flex-1 rounded-none border border-light-bd-muted bg-light-bg-default px-3 text-sm text-light-fg-default outline-none placeholder:text-light-fg-muted focus:border-light-bd-accent"
          name="appliedCode"
          placeholder="Enter your code"
          :aria-describedby="inputDescribedBy"
          :value="input"
          @input="onCompactInput"
          @keydown.enter.prevent="onRedeem"
        />
        <!-- name 必須: autocomplete="off" は Chrome の住所系オートフィルに無視されるため、name で field signature を分ける -->
        <FloatingLabelTextInput
          v-else
          aria-label="Apply Code"
          autocomplete="off"
          label="Apply Code"
          name="appliedCode"
          :aria-describedby="inputDescribedBy"
          :model-value="input"
          :on-update-model-value="onInput"
          :required="false"
          @keydown.enter.prevent="onRedeem"
        />
        <button data-testid="code-redeem-button" type="button" :class="redeemButtonClass" :disabled="!canRedeem" @click="onRedeem">{{ redeemLabel }}</button>
      </div>
      <AtomsErrorText v-if="errorMessage" class="mt-1 block" lang="en" role="alert">{{ errorMessage }}</AtomsErrorText>
      <p v-if="!redemption.isEmailReady" :id="emailRequiredNoteId" class="mt-1 text-sm text-light-fg-muted">Please enter your email address in Contact Information to redeem a code.</p>
    </template>
    <AtomsErrorText v-if="redemption.appliedCodeName && appliedCodeError" class="mt-1 block" lang="en" role="alert">{{ appliedCodeError }}</AtomsErrorText>
  </div>
</template>

<script setup lang="ts">
import { tv } from 'tailwind-variants';
import { computed, nextTick, onMounted, ref, useId, watch } from 'vue';

import AtomsErrorText from '@/components/atoms/ErrorText/ErrorText.vue';
import FloatingLabelTextInput from '@/wabunka-src/common/components/stateful/FloatingLabelTextInput/FloatingLabelTextInput.vue';
import type { ReservationCodeRedemption } from '@/wabunka-src/common/features/Reservation/composables/forms/useReservationCodeRedemption';

interface Props {
  redemption: ReservationCodeRedemption;
  /** 内訳の中に置くときの詰めた見た目。項目名は枠の上に上げず、例示の文字を出す */
  isCompact?: boolean;
  /** 開いた直後から打ち込めるようにする（内訳の Apply Code で開いたとき） */
  shouldFocusOnMount?: boolean;
  /** 適用済みのコードが送信時の再検証で弾かれたときの文言。Next を押した S4 の欄にだけ渡す */
  appliedCodeError?: string;
}

const props = withDefaults(defineProps<Props>(), { isCompact: false, shouldFocusOnMount: false, appliedCodeError: '' });

// 入力しただけでは総額を変えないよう、Redeem を押すまではフォームの state に入れない
const input = ref('');
const errorMessage = ref('');
const compactInput = ref<HTMLInputElement | null>(null);

const canRedeem = computed(() => input.value.trim() !== '' && !props.redemption.isRedeeming && props.redemption.isEmailReady);
const emailRequiredNoteId = useId();
const inputDescribedBy = computed(() => (props.redemption.isEmailReady ? undefined : emailRequiredNoteId));
const redeemLabel = computed(() => (props.redemption.isRedeeming ? 'Processing...' : 'Redeem'));

const onInput = (value: string) => {
  input.value = value;
  errorMessage.value = '';
};
const onCompactInput = (event: Event) => onInput((event.target as HTMLInputElement).value);

const onRedeem = async () => {
  if (!canRedeem.value) return;
  errorMessage.value = await props.redemption.redeem(input.value);
};

const onRemove = () => props.redemption.remove();

// 適用・解除はほかの欄からも起きる。解除して入力欄に戻ったとき、前に打ちかけた文字とエラーが残らないよう捨てる
watch(
  () => props.redemption.appliedCodeName,
  () => {
    input.value = '';
    errorMessage.value = '';
  },
);

onMounted(async () => {
  if (!props.shouldFocusOnMount) return;
  await nextTick();
  compactInput.value?.focus({ preventScroll: true });
});

const redeemButtonStyle = tv({
  base: 'shrink-0 rounded-none border px-4 text-sm tracking-wide transition-colors duration-100 ease-in-out',
  variants: {
    isCompact: {
      true: 'min-w-[86px]',
      false: 'min-w-[104px]',
    },
    canRedeem: {
      true: 'border-transparent text-light-fg-inverse light-bg-accent',
      false: 'cursor-not-allowed border-light-bd-muted bg-light-bg-default text-light-fg-disabled',
    },
  },
});

const redeemButtonClass = computed(() => redeemButtonStyle({ isCompact: props.isCompact, canRedeem: canRedeem.value }));
</script>
